import React, { useState, useEffect } from 'react';

const PlacementPieChart = ({ placementData, salaryData, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [animationPhase, setAnimationPhase] = useState(0);

  // Staggered animation trigger
  useEffect(() => {
    const timers = [
      setTimeout(() => setAnimationPhase(1), 200),
      setTimeout(() => setAnimationPhase(2), 400),
      setTimeout(() => setIsVisible(true), 600)
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // Extract placement percentage from various possible data formats
  const extractPlacementData = (data) => {
    if (!data) return null;

    let placedPercentage = null;
    let totalStudents = null;
    let placedStudents = null;
    let unplacedStudents = null;

    // Handle different data formats
    if (typeof data === 'string') {
      // Look for percentage patterns like "85%", "85 percent", "85% placed"
      const percentMatch = data.match(/(\d+(?:\.\d+)?)\s*%/);
      if (percentMatch) {
        placedPercentage = parseFloat(percentMatch[1]);
      }
      
      // Look for "X out of Y" or "X/Y" patterns
      const fractionMatch = data.match(/(\d+)\s*(?:out\s*of|\/)\s*(\d+)/i);
      if (fractionMatch) {
        placedStudents = parseInt(fractionMatch[1]);
        totalStudents = parseInt(fractionMatch[2]);
        placedPercentage = (placedStudents / totalStudents) * 100;
      }
    } else if (typeof data === 'object') {
      // Handle object format
      if (data.placement_percentage || data.placementPercentage) {
        placedPercentage = parseFloat(data.placement_percentage || data.placementPercentage);
      }
      if (data.total_students || data.totalStudents) {
        totalStudents = parseInt(data.total_students || data.totalStudents);
      }
      if (data.placed_students || data.placedStudents) {
        placedStudents = parseInt(data.placed_students || data.placedStudents);
      }
    } else if (typeof data === 'number') {
      // Assume it's a percentage if it's a number
      if (data <= 100) {
        placedPercentage = data;
      }
    }

    // Validate percentage range
    if (placedPercentage !== null && (placedPercentage < 0 || placedPercentage > 100)) {
      return null;
    }

    // Calculate unplaced percentage
    const unplacedPercentage = placedPercentage !== null ? 100 - placedPercentage : null;

    // Calculate student counts if we have percentage and total
    if (placedPercentage !== null && totalStudents && !placedStudents) {
      placedStudents = Math.round((placedPercentage / 100) * totalStudents);
      unplacedStudents = totalStudents - placedStudents;
    } else if (placedStudents && totalStudents && !unplacedStudents) {
      unplacedStudents = totalStudents - placedStudents;
    }

    return placedPercentage !== null ? {
      placedPercentage: Math.round(placedPercentage * 10) / 10, // Round to 1 decimal
      unplacedPercentage: Math.round(unplacedPercentage * 10) / 10,
      totalStudents,
      placedStudents,
      unplacedStudents
    } : null;
  };

  // Extract salary data for bar chart
  const extractSalaryData = (data) => {
    if (!data) return null;

    let averageSalary = null;
    let highestSalary = null;

    // Handle different data formats
    if (typeof data === 'object') {
      // Extract from object
      averageSalary = data.average_salary || data.averageSalary || data.avg_salary || data.average_package;
      highestSalary = data.highest_salary || data.highestSalary || data.max_salary || data.highest_package || data.max_package;
    }

    // Parse salary values and convert to numbers
    const parseSalary = (salary) => {
      if (!salary) return null;
      if (typeof salary === 'number') return salary;
      
      const salaryStr = String(salary).toLowerCase();
      const numMatch = salaryStr.match(/([\d.]+)/);
      if (!numMatch) return null;
      
      const num = parseFloat(numMatch[1]);
      
      // Convert to LPA if needed
      if (salaryStr.includes('crore') || salaryStr.includes('cr')) {
        return num * 100; // Convert crores to LPA
      } else if (salaryStr.includes('lakh') || salaryStr.includes('lac')) {
        return num / 10; // Convert lakhs to LPA
      } else if (salaryStr.includes('lpa') || salaryStr.includes('per annum')) {
        return num;
      } else if (num > 1000) {
        return num / 100000; // Assume it's in rupees, convert to LPA
      }
      
      return num; // Assume it's already in LPA
    };

    const avgSalaryNum = parseSalary(averageSalary);
    const maxSalaryNum = parseSalary(highestSalary);

    return (avgSalaryNum || maxSalaryNum) ? {
      average: avgSalaryNum,
      highest: maxSalaryNum
    } : null;
  };

  // Generate insights based on data
  const generateInsights = (chartData, salaryData) => {
    const insights = [];
    
    if (chartData) {
      if (chartData.placedPercentage >= 80) {
        insights.push({ type: 'success', text: 'Excellent placement record', icon: '🎯' });
      } else if (chartData.placedPercentage >= 60) {
        insights.push({ type: 'good', text: 'Good placement opportunities', icon: '📈' });
      } else if (chartData.placedPercentage >= 40) {
        insights.push({ type: 'average', text: 'Average placement rate', icon: '📊' });
      } else {
        insights.push({ type: 'low', text: 'Placement needs improvement', icon: '📉' });
      }
    }
    
    if (salaryData && salaryData.average && salaryData.highest) {
      const ratio = salaryData.highest / salaryData.average;
      if (ratio > 3) {
        insights.push({ type: 'info', text: 'Wide salary range indicates diverse opportunities', icon: '💼' });
      } else if (ratio > 2) {
        insights.push({ type: 'info', text: 'Good salary growth potential', icon: '📊' });
      } else {
        insights.push({ type: 'info', text: 'Consistent salary packages', icon: '💰' });
      }
    }
    
    return insights;
  };

  const chartData = extractPlacementData(placementData);
  const salaryChartData = extractSalaryData(salaryData);
  const insights = generateInsights(chartData, salaryChartData);

  // Don't render if no valid data
  if (!chartData && !salaryChartData) {
    return null;
  }

  // Calculate chart variables only if chartData exists
  let radius, centerX, centerY, placedAngle, getPath, polarToCartesian;
  
  if (chartData) {
    radius = 45;
    centerX = 50;
    centerY = 50;
    placedAngle = (chartData.placedPercentage / 100) * 360;
    
    getPath = (startAngle, endAngle) => {
      const start = polarToCartesian(centerX, centerY, radius, endAngle);
      const end = polarToCartesian(centerX, centerY, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      
      return [
        "M", centerX, centerY,
        "L", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        "Z"
      ].join(" ");
    };

    polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
      const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      };
    };
  }

  return (
    <div className={`relative bg-gradient-to-br from-slate-50 via-white to-blue-50/30 rounded-xl sm:rounded-2xl lg:rounded-3xl border border-slate-200/60 shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-500 p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8 backdrop-blur-sm ${className}`}>
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100/40 to-blue-100/40 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-100/40 to-emerald-100/40 rounded-full blur-2xl -z-10"></div>
      
      {/* Premium Header */}
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4 sm:mb-6 lg:mb-8 xl:mb-10 transition-all duration-700 ${animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="flex items-center min-w-0">
          <div className="relative p-2 sm:p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg sm:rounded-2xl shadow-lg flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-lg sm:rounded-2xl"></div>
            <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="ml-2 sm:ml-4 min-w-0 flex-1">
            <h4 className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent truncate">
              Placement Analytics
            </h4>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5 truncate">Performance Overview & Insights</p>
          </div>
        </div>
        
        {/* Status Badge */}
        {chartData && (
          <div className={`self-start sm:self-center px-2 sm:px-3 lg:px-4 py-1 sm:py-2 rounded-full text-xs font-bold shadow-sm transition-all duration-300 whitespace-nowrap ${
            chartData.placedPercentage >= 80 
              ? 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 border border-emerald-200' 
              : chartData.placedPercentage >= 60
              ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 border border-blue-200'
              : 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border border-amber-200'
          }`}>
            {chartData.placedPercentage >= 80 ? 'Excellent' : chartData.placedPercentage >= 60 ? 'Good' : 'Average'}
          </div>
        )}
      </div>
      
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8 transition-all duration-700 delay-200 ${animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        {/* Premium Pie Chart Section */}
        {chartData && (
          <div className="relative bg-gradient-to-br from-white to-slate-50/50 rounded-lg sm:rounded-xl lg:rounded-2xl border border-slate-200/60 shadow-md sm:shadow-lg hover:shadow-xl transition-all duration-300 p-3 sm:p-4 lg:p-5 xl:p-6">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-emerald-100/30 to-green-100/30 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-4 sm:mb-6">
                <h5 className="text-sm sm:text-base lg:text-lg font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent truncate">
                  Placement Overview
                </h5>
                <div className={`self-start sm:self-center px-2 sm:px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                  chartData.placedPercentage >= 80 ? 'bg-emerald-100 text-emerald-700' :
                  chartData.placedPercentage >= 60 ? 'bg-blue-100 text-blue-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {chartData.placedPercentage}%
                </div>
              </div>
              
              <div className="flex flex-col items-center">
                {/* Enhanced Pie Chart */}
                <div className="relative mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-100/50 to-transparent rounded-full blur-sm"></div>
                  <svg 
                    width="100" 
                    height="100" 
                    viewBox="0 0 100 100" 
                    className="transform -rotate-90 w-[100px] h-[100px] sm:w-[120px] sm:h-[120px] md:w-[140px] md:h-[140px] lg:w-[160px] lg:h-[160px] drop-shadow-lg relative z-10 mx-auto"
                  >
                    {/* Enhanced background circle */}
                    <circle
                      cx={centerX}
                      cy={centerY}
                      r={radius}
                      fill="url(#bgGradient)"
                      stroke="#f1f5f9"
                      strokeWidth="1"
                    />
                    
                    {/* Gradient definitions */}
                    <defs>
                      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f8fafc" />
                        <stop offset="100%" stopColor="#f1f5f9" />
                      </linearGradient>
                      <linearGradient id="placedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#059669" />
                      </linearGradient>
                      <linearGradient id="unplacedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ef4444" />
                        <stop offset="100%" stopColor="#dc2626" />
                      </linearGradient>
                    </defs>
                    
                    {/* Placed segment with enhanced styling */}
                    {chartData.placedPercentage > 0 && (
                      <path
                        d={getPath(0, isVisible ? placedAngle : 0)}
                        fill="url(#placedGradient)"
                        stroke="#ffffff"
                        strokeWidth="2"
                        className="transition-all duration-1200 ease-out cursor-pointer"
                        onMouseEnter={() => setHoveredSlice('placed')}
                        onMouseLeave={() => setHoveredSlice(null)}
                        style={{
                          filter: hoveredSlice === 'placed' ? 'drop-shadow(0 6px 12px rgba(16, 185, 129, 0.4)) brightness(1.1)' : 'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.2))',
                          transform: hoveredSlice === 'placed' ? 'scale(1.03)' : 'scale(1)',
                          transformOrigin: '50% 50%'
                        }}
                      />
                    )}
                    
                    {/* Unplaced segment with enhanced styling */}
                    {chartData.unplacedPercentage > 0 && (
                      <path
                        d={getPath(isVisible ? placedAngle : 0, isVisible ? 360 : 0)}
                        fill="url(#unplacedGradient)"
                        stroke="#ffffff"
                        strokeWidth="2"
                        className="transition-all duration-1200 ease-out cursor-pointer"
                        onMouseEnter={() => setHoveredSlice('unplaced')}
                        onMouseLeave={() => setHoveredSlice(null)}
                        style={{
                          filter: hoveredSlice === 'unplaced' ? 'drop-shadow(0 6px 12px rgba(239, 68, 68, 0.4)) brightness(1.1)' : 'drop-shadow(0 2px 4px rgba(239, 68, 68, 0.2))',
                          transform: hoveredSlice === 'unplaced' ? 'scale(1.03)' : 'scale(1)',
                          transformOrigin: '50% 50%'
                        }}
                      />
                    )}
                  </svg>
                  
                  {/* Enhanced Center Text */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black bg-gradient-to-br from-slate-800 to-slate-600 bg-clip-text text-transparent">
                      {chartData.placedPercentage}%
                    </div>
                    <div className="text-xs sm:text-sm text-slate-500 font-semibold tracking-wide uppercase">
                      Placed
                    </div>
                    {chartData.totalStudents && (
                      <div className="text-xs text-slate-400 mt-1">
                        of {chartData.totalStudents}
                      </div>
                    )}
                  </div>
                  
                  {/* Enhanced Tooltip */}
                  {hoveredSlice && (
                    <div className="absolute -top-14 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap z-[9999] shadow-xl">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          hoveredSlice === 'placed' ? 'bg-emerald-400' : 'bg-red-400'
                        }`}></div>
                        <span>
                          {hoveredSlice === 'placed' 
                            ? `${chartData.placedPercentage}% Placed${chartData.placedStudents ? ` (${chartData.placedStudents} students)` : ''}` 
                            : `${chartData.unplacedPercentage}% Unplaced${chartData.unplacedStudents ? ` (${chartData.unplacedStudents} students)` : ''}`
                          }
                        </span>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                    </div>
                  )}
                </div>
            
                {/* Premium Legend */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-md mx-auto">
                  <div className="group bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-emerald-200/60 transition-all duration-300 hover:shadow-lg hover:scale-105 cursor-pointer min-h-[90px] flex flex-col justify-center items-center text-center">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full shadow-sm group-hover:shadow-md transition-shadow flex-shrink-0"></div>
                      <span className="text-sm font-bold text-emerald-800">Placed</span>
                    </div>
                    <div className="text-lg font-black text-emerald-900">{chartData.placedPercentage}%</div>
                    {chartData.placedStudents && (
                      <div className="text-xs text-emerald-600 font-medium">{chartData.placedStudents} students</div>
                    )}
                  </div>
                  
                  <div className="group bg-gradient-to-br from-red-50 to-rose-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-red-200/60 transition-all duration-300 hover:shadow-lg hover:scale-105 cursor-pointer min-h-[90px] flex flex-col justify-center items-center text-center">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-red-500 to-rose-500 rounded-full shadow-sm group-hover:shadow-md transition-shadow flex-shrink-0"></div>
                      <span className="text-sm font-bold text-red-800">Unplaced</span>
                    </div>
                    <div className="text-lg font-black text-red-900">{chartData.unplacedPercentage}%</div>
                    {chartData.unplacedStudents && (
                      <div className="text-xs text-red-600 font-medium">{chartData.unplacedStudents} students</div>
                    )}
                  </div>
                </div>
                
                {/* Summary Stats */}
                {chartData.totalStudents && (
                  <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-slate-200">
                    <div className="text-center bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Total Batch Size</div>
                      <div className="text-lg sm:text-xl font-black text-slate-800">{chartData.totalStudents}</div>
                      <div className="text-xs text-slate-500">students</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Premium Bar Chart Section */}
        {salaryChartData && (salaryChartData.average || salaryChartData.highest) && (
          <div className="relative bg-gradient-to-br from-white to-slate-50/50 rounded-lg sm:rounded-xl lg:rounded-2xl border border-slate-200/60 shadow-md sm:shadow-lg hover:shadow-xl transition-all duration-300 p-3 sm:p-4 lg:p-5 xl:p-6">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100/30 to-indigo-100/30 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-4 sm:mb-6">
                <h5 className="text-sm sm:text-base lg:text-lg font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent truncate">
                  Salary Analysis
                </h5>
                {salaryChartData.average && salaryChartData.highest && (
                  <div className="self-start sm:self-center text-xs font-bold text-slate-600 bg-slate-100 px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">
                    Top is {(salaryChartData.highest / salaryChartData.average).toFixed(1)}× higher
                  </div>
                )}
              </div>
              
              {/* Enhanced Bar Chart Container */}
              <div className="relative bg-gradient-to-b from-slate-50/50 to-transparent rounded-xl p-3 sm:p-4 pt-8">
                {/* Premium Grid Lines */}
                <div className="absolute inset-3 sm:inset-4 top-8 flex flex-col justify-between opacity-30 pointer-events-none">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="border-t border-slate-300 border-dashed"></div>
                  ))}
                </div>
                
                <div className="relative flex items-end justify-center gap-8 sm:gap-12 lg:gap-16 pt-4 sm:pt-6 pb-2">
                  {/* Enhanced Average Salary Bar */}
                  {salaryChartData.average && (
                    <div className="flex flex-col items-center group">
                      {/* Premium Value Label */}
                      <div className="mb-3 sm:mb-4 text-center transform transition-all duration-300 group-hover:scale-110">
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-2 sm:px-3 lg:px-4 py-1 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold shadow-lg">
                          ₹{salaryChartData.average.toFixed(1)} LPA
                        </div>
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-8 border-transparent border-t-blue-500 mx-auto"></div>
                      </div>
                      
                      {/* Enhanced Bar */}
                      <div className="relative">
                        <div 
                          className="bg-gradient-to-t from-blue-600 via-blue-500 to-blue-400 rounded-t-2xl w-12 sm:w-16 lg:w-20 transition-all duration-1000 ease-out shadow-xl cursor-pointer relative overflow-hidden"
                          style={{
                            height: isVisible ? `${Math.max((salaryChartData.average / Math.max(salaryChartData.average || 0, salaryChartData.highest || 0)) * 80, 20)}px` : '0px',
                            transform: hoveredBar === 'average' ? 'scale(1.05) translateY(-2px)' : 'scale(1)'
                          }}
                          onMouseEnter={() => setHoveredBar('average')}
                          onMouseLeave={() => setHoveredBar(null)}
                        >
                          {/* Enhanced shine effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 rounded-t-2xl animate-pulse"></div>
                          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-300 to-blue-200 rounded-t-2xl"></div>
                        </div>
                        
                        {/* Enhanced Tooltip */}
                        {hoveredBar === 'average' && (
                          <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap shadow-2xl" style={{zIndex: 99999}}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                              <span>Average: ₹{salaryChartData.average.toFixed(1)} LPA</span>
                            </div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                          </div>
                        )}
                      </div>
                      
                      {/* Enhanced Label */}
                      <div className="mt-3 sm:mt-4 text-center">
                        <div className="text-xs sm:text-sm lg:text-base font-bold text-slate-700">Average</div>
                        <div className="text-xs text-slate-500 font-medium">Package</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Enhanced Highest Salary Bar */}
                  {salaryChartData.highest && (
                    <div className="flex flex-col items-center group">
                      {/* Premium Value Label */}
                      <div className="mb-3 sm:mb-4 text-center transform transition-all duration-300 group-hover:scale-110">
                        <div className="bg-gradient-to-r from-emerald-500 to-green-500 text-white px-2 sm:px-3 lg:px-4 py-1 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold shadow-lg">
                          ₹{salaryChartData.highest.toFixed(1)} LPA
                        </div>
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-8 border-transparent border-t-emerald-500 mx-auto"></div>
                      </div>
                      
                      {/* Enhanced Bar */}
                      <div className="relative">
                        <div 
                          className="bg-gradient-to-t from-emerald-600 via-emerald-500 to-emerald-400 rounded-t-2xl w-12 sm:w-16 lg:w-20 transition-all duration-1000 ease-out shadow-xl cursor-pointer relative overflow-hidden"
                          style={{
                            height: isVisible ? `${Math.max((salaryChartData.highest / Math.max(salaryChartData.average || 0, salaryChartData.highest || 0)) * 80, 20)}px` : '0px',
                            transform: hoveredBar === 'highest' ? 'scale(1.05) translateY(-2px)' : 'scale(1)'
                          }}
                          onMouseEnter={() => setHoveredBar('highest')}
                          onMouseLeave={() => setHoveredBar(null)}
                        >
                          {/* Enhanced shine effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 rounded-t-2xl animate-pulse"></div>
                          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-300 to-emerald-200 rounded-t-2xl"></div>
                        </div>
                        
                        {/* Enhanced Tooltip */}
                        {hoveredBar === 'highest' && (
                          <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 bg-slate-900 text-white px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap shadow-2xl" style={{zIndex: 99999}}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0"></div>
                              <span>Highest: ₹{salaryChartData.highest.toFixed(1)} LPA</span>
                            </div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                          </div>
                        )}
                      </div>
                      
                      {/* Enhanced Label */}
                      <div className="mt-3 sm:mt-4 text-center">
                        <div className="text-xs sm:text-sm lg:text-base font-bold text-slate-700">Highest</div>
                        <div className="text-xs text-slate-500 font-medium">Package</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementPieChart;